
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { productsData } from '../../../mocks/products';

interface ProductCategoriesProps {
  onDownloadCatalogue: () => void;
  onGetQuote?: () => void;
}

const categories = productsData.map((product) => ({
  icon: product.icon,
  name: product.name,
  shortName: product.name.split(' ')[0],
  description: product.description.split('.')[0],
  color: 'teal',
  images: product.images,
  filter: product.category,
}));

const filters = [
  { id: 'all', label: 'All' },
  { id: 'laser-toner-cartridges', label: 'Toner' },
  { id: 'generic-ink', label: 'Ink' },
  { id: 'opc-drums', label: 'Drums' },
  { id: 'toner-powder', label: 'Powder' },
  { id: 'lamination-pouch', label: 'Pouch' },
  { id: 'lamination-machine', label: 'Machine' },
  { id: 'glossy-photo-paper', label: 'Photo Paper' },
  { id: 'printer-parts', label: 'Parts' },
  { id: 'inkjet-pvc-card', label: 'PVC Cards' },
];

// Image Carousel Component
function ImageCarousel({ 
  images, 
  name, 
  isMobile = false 
}: { 
  images: string[]; 
  name: string; 
  isMobile?: boolean;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll on hover (desktop only)
  useEffect(() => {
    if (!isMobile && isHovering && images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, 1000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovering, images.length, isMobile]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // Swipe left - next image
        setCurrentIndex((prev) => (prev + 1) % images.length);
      } else {
        // Swipe right - previous image
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovering(false);
      setCurrentIndex(0);
    }
  };

  return (
    <div
      className="relative w-full aspect-square bg-gray-50 overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchMove={isMobile ? handleTouchMove : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      <img
        src={images[currentIndex]}
        alt={name}
        className={`w-full h-full object-cover transition-transform duration-500 ${
          isHovering && !isMobile ? 'scale-110' : ''
        }`}
      />
      
      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                idx === currentIndex
                  ? 'w-4 bg-teal-600'
                  : 'w-1.5 bg-white/70 hover:bg-white'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductCategories({ onDownloadCatalogue, onGetQuote }: ProductCategoriesProps) {
  const [activeFilter, setActiveFilter] = useState('all');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const filteredCategories = activeFilter === 'all' 
    ? categories 
    : categories.filter(cat => cat.filter === activeFilter);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  return (
    <section className="py-12 md:py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-6 md:mb-12 space-y-2 md:space-y-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
            Our Product Range
          </h2>
          <p className="text-base md:text-lg text-gray-600">
            Complete B2B supply for printer consumables and lamination needs
          </p>
        </div>

        {/* Filter Chips - Mobile Horizontal Scroll */}
        <div className="mb-6 md:mb-8 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-max md:justify-center px-1">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  activeFilter === filter.id
                    ? 'bg-teal-700 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Carousel (below 768px) */}
        <div className="md:hidden">
          <div 
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4 cursor-grab active:cursor-grabbing"
            style={{ 
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {filteredCategories.map((category, index) => (
              <div
                key={index}
                className="flex-none w-[calc(50%-6px)] snap-start"
              >
                <div className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-teal-600 hover:shadow-lg transition-all duration-300 h-full">
                  {/* Image Carousel */}
                  <ImageCarousel 
                    images={category.images} 
                    name={category.name} 
                    isMobile={true}
                  />

                  {/* Content */}
                  <div className="p-3 space-y-2">
                    <h3 className="text-sm font-bold text-gray-900 line-clamp-2 leading-tight">
                      {category.name}
                    </h3>
                    <p className="text-xs text-gray-600 line-clamp-1">
                      {category.description}
                    </p>
                    <Link
                      to="/products"
                      className="inline-block w-full text-center px-3 py-2 bg-teal-700 hover:bg-teal-800 text-white text-xs font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Get Quote
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Link */}
          <div className="text-center mt-6">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-teal-700 font-semibold hover:gap-3 transition-all duration-300 cursor-pointer text-sm"
            >
              View All Products
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Desktop Grid (768px and above) */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredCategories.map((category, index) => (
            <div
              key={index}
              onClick={onGetQuote}
              className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-teal-600 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Image Carousel */}
              <ImageCarousel 
                images={category.images} 
                name={category.name} 
                isMobile={false}
              />

              {/* Content */}
              <div className="p-6 space-y-3">
                <h3 className="text-xl font-bold text-gray-900">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {category.description}
                </p>

                {/* CTA */}
                <div className="inline-flex items-center gap-2 text-teal-700 font-semibold group-hover:gap-3 transition-all duration-300 whitespace-nowrap">
                  Request Pricing
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA - Desktop Only */}
        <div className="hidden md:block text-center pt-8">
          <button
            onClick={onDownloadCatalogue}
            className="group px-8 py-4 bg-white border-2 border-teal-700 text-teal-700 hover:bg-teal-700 hover:text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center gap-3 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-download-line text-xl"></i>
            Download Complete Catalogue
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
          </button>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}
